import { useEffect, useState } from "react";
import { Plus, Trash, PencilSimple, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", bank_name: "", property_types: [], clauses: "" };

export default function ReportTemplates() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/coll/report_templates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const togglePt = (t) => {
    const cur = form.property_types || [];
    setForm({ ...form, property_types: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/coll/report_templates/${editing}`, form);
      else await api.post("/coll/report_templates", form);
      toast.success(editing ? "Template updated" : "Template added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/coll/report_templates/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading templates…</div>;

  return (
    <div className="space-y-6" data-testid="report-templates-page">
      <PageHeader
        overline="Valuation Jobs" title="Report Templates"
        subtitle="Many banks mandate their own report format — keep them here so drafting stays fast and compliant"
        action={<Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-template-btn"><Plus size={16} className="mr-1" /> Add Template</Button>}
      />

      {items.length === 0 ? (
        <EmptyState icon={FileText}>No templates yet. Add a bank-specific report format with its standard clauses.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><FileText size={18} weight="duotone" className="text-accent shrink-0" /><h3 className="font-display font-semibold text-lg text-primary truncate">{t.name}</h3></div>
                  <div className="text-xs text-accent mt-0.5">{t.bank_name || "Any bank / generic"}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { setEditing(t.id); setForm({ ...EMPTY, ...t }); setOpen(true); }} data-testid={`edit-template-${t.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`}><Trash size={14} /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(t.property_types || []).map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">{p}</span>)}
              </div>
              {t.clauses && <p className="text-sm text-muted-foreground mt-3 line-clamp-4 whitespace-pre-wrap">{t.clauses}</p>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Template" : "Add Report Template"}</DialogTitle>
            <DialogDescription>Store a bank's mandated format and the standard clauses you reuse.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="template-form">
            <div className="space-y-1.5"><Label>Template Name *</Label><Input required value={form.name} onChange={set("name")} placeholder="e.g. SBI Mortgage Valuation Format" data-testid="template-name" /></div>
            <div className="space-y-1.5"><Label>Bank (leave blank for generic)</Label><Input value={form.bank_name} onChange={set("bank_name")} data-testid="template-bank" /></div>
            <div>
              <Label className="mb-2 block">Applicable Property Types</Label>
              <div className="flex flex-wrap gap-2">
                {PROPERTY_TYPES.map((p) => {
                  const on = (form.property_types || []).includes(p);
                  return (
                    <button type="button" key={p} onClick={() => togglePt(p)} data-testid={`template-pt-${p.replace(/\W+/g, "-")}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${on ? "bg-accent text-accent-foreground border-accent" : "bg-white text-muted-foreground border-border hover:border-accent"}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Standard Clauses & Disclaimers</Label><Textarea rows={6} value={form.clauses} onChange={set("clauses")} data-testid="template-clauses" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-template-btn">{editing ? "Update" : "Add"} Template</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
